//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import { theme } from '@bvengineering/unison-core';

import { UniversalScheduledPostConfigType } from '@apis/universal-posts/config/universalScheduledPostConfigModel';
import {
  MAX_CAROUSEL_PIN_IMAGES,
  MAX_PIN_DESCRIPTION_LENGTH,
  MAX_PIN_TITLE_LENGTH,
  MIN_MEDIA_HEIGHT_PX,
  MIN_MEDIA_WIDTH_PX,
  PinPostingIntervalValue,
  PinterestPostAttemptError,
  PinterestPostDetailsSchedulingStep,
} from '@pinterest/redux/post-details/pinterestPostDetailsModel';

export const PINTEREST_POST_PREVIEW_MEDIA_WIDTH = 479;
export const PINTEREST_POST_PREVIEW_MEDIA_MAX_HEIGHT = 760;
export const PINTEREST_POST_DETAILS_BORDER_STYLE = `${theme.borderWidths[1]} solid ${theme.colors.gray2}`;

export const PINTEREST_BULK_PIN_THUMBNAIL_MEDIA_WIDTH = 84;

// Thumbnail media width + 16px padding + 2px for border
export const PINTEREST_BULK_PIN_THUMBNAIL_WIDTH =
  PINTEREST_BULK_PIN_THUMBNAIL_MEDIA_WIDTH + 16 + 2;

export const PINTEREST_BULK_PREVIEW_GRID_MEDIA_WIDTH = 148;

export const PostDetailsBulkScheduleHeaderText: Record<
  PinterestPostDetailsSchedulingStep,
  string
> = {
  [PinterestPostDetailsSchedulingStep.MediaDetails]: 'Media & Details',
  [PinterestPostDetailsSchedulingStep.ScheduleDetails]: 'Schedule Pins',
} as const;

export const PostDetailsBulkSchedulePostingIntervalLabel: Record<
  PinPostingIntervalValue,
  string
> = {
  [0]: 'Post all pins at the same time',
  [15]: 'Every 15 minutes',
  [30]: 'Every 30 minutes',
  [45]: 'Every 45 minutes',
  [60]: 'Every 1 hour',
  [120]: 'Every 2 hours',
} as const;

export const BusinessCarouselTooltip =
  "To create a carousel post, you'll need to convert your Pinterest account to a business account.";
export const VideoCarouselTooltip = `Carousel posts cannot contain videos. If you'd like to create a carousel post, remove the video and add images instead.`;
export const GifCarouselTooltip = `Carousel posts cannot contain GIFs. If you'd like to create a carousel post, remove the GIF and add images instead.`;
export const MaxCarouselImagesTooltip = `Carousel posts can only contain up to ${MAX_CAROUSEL_PIN_IMAGES} images.`;

export const PinterestPostDetailsFormFieldErrorText = {
  // Bulk Board Select
  NoBulkBoardSelected: 'Please select a board to add all pins to.',

  // Pin Media & Details
  DescriptionTooLong: `Please enter ${MAX_PIN_DESCRIPTION_LENGTH} characters or less.`,
  InvalidDestinationLink:
    'Please input a valid URL (http(s)://www.example.com).',
  MediaTooSmall: `The media you selected cannot be scheduled because it does not meet
  Pinterest's image size requirements. Please use an image larger than
  ${MIN_MEDIA_WIDTH_PX}x${MIN_MEDIA_HEIGHT_PX}px.`,
  NoMedia: 'Please add media to this pin.',
  NoBoard: 'Please select a board.',
  NoPostDate: 'Please select a date.',
  NoPostTime: 'Please select a time.',
  PostTimeInPast: 'Please select a post time in the future.',
  TitleTooLong: `Please enter ${MAX_PIN_TITLE_LENGTH} characters or less.`,
  VideoTooLong: `The video you selected cannot be scheduled. Please reduce its length
  to less than 15 minutes and try again.`,

  // Bulk Schedule Details
  NoDayOfWeek: 'Please select at least one day of the week.',
  NoPinsPerDay:
    'Please select how many pins per day you would like to schedule.',
  NoPostingInterval: 'Please select a time interval.',
  NoStartPostDate: 'Please select a date.',
  NoStartPostTime: 'Please select a time.',
  StartPostTimeInPast: 'Please select a time in the future.',
} as const;

export const getPostDetailsFailureStatusMessage = (
  error: PinterestPostAttemptError,
) => {
  switch (error) {
    case PinterestPostAttemptError.Authentication:
      return 'The token used to publish this post has expired. Please go to Account Settings to reconnect your Pinterest account, then update the scheduled time and try again.';
    case PinterestPostAttemptError.BoardAuthorization:
      return 'You no longer have access to post to this board.';
    case PinterestPostAttemptError.Credentials:
      return 'The token used to publish this post has expired. Please go to Account Settings to reconnect your Pinterest account, then update the scheduled time and try again.';
    case PinterestPostAttemptError.MaxAttempts:
      return 'Something went wrong. Please update the scheduled time and try again. If the issue persists, please contact Bazaarvoice Support to resolve this error.';
    case PinterestPostAttemptError.SourceDeleted:
      return 'The content you are trying to schedule no longer exists! Try saving a different piece of content.';
    case PinterestPostAttemptError.NoBoard:
    case PinterestPostAttemptError.NotFound:
      return 'The board you are trying to schedule to no longer exists. Try scheduling to a different board instead.';
    case PinterestPostAttemptError.SmallImage:
      return 'This image is too small to be posted. Please try using using an image that is 120x120 or larger.';
    case PinterestPostAttemptError.LargeImage:
      return 'This image is too large to be posted. Please try using using an image that is 32mb or smaller.';
    case PinterestPostAttemptError.BrokenImage:
      return 'Holy moly, Pinterest says that image is broken! Try using a different image.';
    case PinterestPostAttemptError.PostedBrokenImage:
      return 'Pinterest is reporting that the content you tried to schedule has broken! Please verify on Pinterest.com before rescheduling.';
    case PinterestPostAttemptError.WritesBlocked:
      return 'Pinterest has put your account in Safe Mode and is not allowing new pins. Please change your Pinterest password and reenter your new credentials on the settings page.';
    case PinterestPostAttemptError.InvalidSource:
      return 'Pinterest does not allow pins linking to this domain. Try scheduling your pin with a different URL.';
    case PinterestPostAttemptError.InvalidParameters:
      return 'Pinterest has prevented your post from publishing due to one or more pin fields being invalid. Check that the URL is valid and the description is less than 500 characters.';
    case PinterestPostAttemptError.Content:
      return 'Pinterest has prevented your post from publishing due concern that your URL is spammy or inappropriate. Try scheduling your pin with a different URL.';
    case PinterestPostAttemptError.PushNotification:
      return 'We failed to send a push notification to your mobile device. Try logging out of the Bazaarvoice Vibe mobile app and logging back in, then reschedule your post.';
    case PinterestPostAttemptError.PushToken:
      return 'We failed to send a push notification to your mobile device. Try logging out of the Bazaarvoice Vibe mobile app and logging back in, then reschedule your post.';
    case PinterestPostAttemptError.Device:
      return 'We failed to send a push notification to your mobile device. Try logging out of the Bazaarvoice Vibe mobile app and logging back in, then reschedule your post.';
    case PinterestPostAttemptError.RequestTimeout:
      return 'Pinterest sent a request timeout when we attempted to publish your pin. Please verify on Pinterest.com that the pin does not already exist before rescheduling.';
    default:
      return GenericPostErrorMessage;
  }
};

export const PostDetailsDraftErrorMessages = {
  RequestSaveDraftPinFailed: `Failed to save draft post type: ${UniversalScheduledPostConfigType.PinterestOrganicPin}`,
  RequestLoadDraftPinFailed: 'Failed to load existing Pinterest draft post',
  RequestDeletePinFailed: 'Failed to delete Pinterest draft post',
};

export const PostDetailsScheduleErrorMessages = {
  RequestSchedulePinFailed: `Failed to schedule Pinterest post.`,
};

export const GenericPostErrorMessage =
  'Something went wrong. Please update the scheduled time and try again.';
